/**
 * Устойчивые ленивые чанки. React.lazy кэширует ПЕРВЫЙ исход import() навсегда:
 * один сетевой сбой/протухший после деплоя чанк — и панель остаётся пустым
 * Suspense-фолбэком (или роняет роут) до ручного F5. Здесь: ретрай сетевого
 * сбоя, затем одна автоматическая перезагрузка страницы (свежий HTML получает
 * новые URL чанков), с sessionStorage-гвардом от цикла перезагрузок. Нет гварда,
 * который переживёт reload, — нет и перезагрузки (см. `writeGuard`).
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RETRY_DELAY_MS = 500;
const GUARD_PREFIX = 'chunk-reload:';

/** Гвард в памяти: дедуп в пределах одной загрузки страницы. Перезагрузку он не переживает. */
let memoryGuard = new Set<string>();

export function __resetReloadGuardForTests(): void {
  memoryGuard = new Set();
}

function readGuard(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1' || memoryGuard.has(key);
  } catch {
    return memoryGuard.has(key);
  }
}

/**
 * Ставит гвард. `false` — sessionStorage недоступен (куки заблокированы, приватный режим),
 * то есть отметка не переживёт перезагрузку. Перезагружаться в таком случае нельзя: гвард
 * в памяти стирается вместе со страницей, и постоянная ошибка импорта закрутила бы
 * бесконечный цикл reload. Ошибка наружу неприятна, вечная перезагрузка — хуже.
 */
function writeGuard(key: string): boolean {
  memoryGuard.add(key);
  try {
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

function clearGuard(key: string): void {
  memoryGuard.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * import() с ретраем и one-shot перезагрузкой. `chunkName` — стабильный ключ
 * гварда (по одному на чанк, чтобы разные чанки не съедали чужую попытку).
 * `reload` — инжектируемый колбэк перезагрузки (по умолчанию
 * `window.location.reload`); вынесен параметром, а не читается из
 * `window.location` напрямую в теле функции, чтобы тесты подменяли поведение
 * без мока non-configurable `window.location` (throws в jsdom 29+).
 */
export async function importWithRetry<T>(
  factory: () => Promise<T>,
  chunkName: string,
  reload: () => void = () => window.location.reload(),
): Promise<T> {
  const guardKey = GUARD_PREFIX + chunkName;
  try {
    const loaded = await factory();
    clearGuard(guardKey);
    return loaded;
  } catch {
    await wait(RETRY_DELAY_MS);
  }
  try {
    const loaded = await factory();
    clearGuard(guardKey);
    return loaded;
  } catch (err) {
    if (!readGuard(guardKey) && writeGuard(guardKey)) {
      reload();
      // Замораживаем lazy до перезагрузки — без мигания error-фолбэка.
      return await new Promise<never>(() => {});
    }
    throw err;
  }
}

/** React.lazy поверх importWithRetry — drop-in замена React.lazy для панелей. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- паритет с типизацией React.lazy
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  chunkName: string,
): LazyExoticComponent<T> {
  return lazy(() => importWithRetry(factory, chunkName));
}
